"use client";

import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw, Search } from "lucide-react";
import PermissionGuard from "@/components/PermissionGuard";
import { PermissionSubject } from "@/utils/permissions";
import { useCategoryStore } from "@/stores/categoryStore";
import { useDebounce } from "@/hooks/useDebounce";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import ModalCreateCategory from "@/components/category/ModalCreateCategory";
import ModalViewCategory from "@/components/category/ModalViewCategory";
import ModalEditCategory from "@/components/category/ModalEditCategory";
import ModalDeleteCategory from "@/components/category/ModalDeleteCategory";

export default function CategoryPage() {
  const {
    categories,
    pagination,
    isLoading,
    fetchCategories,
    roots,
    fetchRoots,
  } = useCategoryStore();

  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 500);
  const [openCreate, setOpenCreate] = useState(false);
  const isSearching = search !== debounced;
  const [selected, setSelected] = useState<any>(null);
  const [openView, setOpenView] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  useEffect(() => {
    fetchCategories({ page: 1, limit: 10, search: debounced });
    fetchRoots();
  }, [debounced, fetchCategories, fetchRoots]);

  const handleRefresh = () => {
    fetchCategories({
      page: pagination.page,
      limit: pagination.limit,
      search: debounced,
    });
  };

  const handlePage = (page: number) => {
    fetchCategories({ page, limit: pagination.limit, search: debounced });
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    fetchCategories({ page: 1, limit: pagination.limit, search });
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
                Quản lý danh mục
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <p className="text-gray-500">
                  Tổ chức danh mục theo cấu trúc 3 cấp
                </p>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                  {pagination.total} danh mục
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="flex items-center gap-2 hover:cursor-pointer"
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
              Làm mới
            </Button>
            <PermissionGuard
              subject={PermissionSubject.CATEGORY}
              action="create"
            >
              <Button
                className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-4 py-2 flex items-center gap-2 hover:cursor-pointer"
                onClick={() => setOpenCreate(true)}
              >
                <Plus size={20} />
                Thêm danh mục
              </Button>
            </PermissionGuard>
          </div>
        </div>

        <div className="mb-6">
          <form
            onSubmit={handleSearch}
            className="grid grid-cols-1 md:grid-cols-6 gap-3"
          >
            <div className="md:col-span-3 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tìm kiếm theo tên/slug/mô tả..."
                className="h-10 w-full pl-10 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <Select
                onValueChange={(v: string) =>
                  fetchCategories({
                    page: 1,
                    limit: 10,
                    search: debounced,
                    level: v === "all" ? undefined : Number(v),
                  })
                }
                defaultValue="all"
              >
                <SelectTrigger className="h-10 w-full">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả vị trí</SelectItem>
                  <SelectItem value="0">Gốc</SelectItem>
                  <SelectItem value="1">Cấp 1</SelectItem>
                  <SelectItem value="2">Cấp 2</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select
                onValueChange={(v: string) =>
                  fetchCategories({
                    page: 1,
                    limit: 10,
                    search: debounced,
                    parent: v === "all" ? undefined : v,
                  })
                }
                defaultValue="all"
              >
                <SelectTrigger className="h-10 w-full">
                  <SelectValue placeholder="Danh mục cha" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả danh mục cha</SelectItem>
                  {roots.map((r) => (
                    <SelectItem key={r._id} value={r._id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1 flex items-stretch justify-end">
              <Button
                type="submit"
                disabled={isLoading || isSearching}
                className="h-10 w-full md:w-auto hover:cursor-pointer"
              >
                {isSearching ? "Đang tìm..." : "Tìm kiếm"}
              </Button>
            </div>
          </form>
        </div>

        <div className="shadow-lg rounded-xl bg-white border border-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Đang tải...</span>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Tên
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Slug
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Canonical URL
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Từ khóa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Danh mục cha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Vị trí
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {categories.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-16">
                          <div className="flex flex-col items-center justify-center text-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              className="h-12 w-12 text-gray-300 mb-3"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                                d="M3 7.5A2.25 2.25 0 0 1 5.25 5.25h13.5A2.25 2.25 0 0 1 21 7.5v9.75A1.5 1.5 0 0 1 19.5 18.75H4.5A1.5 1.5 0 0 1 3 17.25V7.5Zm3.75 0v-1.5m10.5 1.5v-1.5M6.75 10.5h10.5"
                              />
                            </svg>
                            <p className="text-gray-600 font-medium">
                              Chưa có danh mục
                            </p>
                            <p className="text-gray-400 text-sm mt-1">
                              Hãy thêm danh mục đầu tiên để bắt đầu
                            </p>
                            <PermissionGuard
                              subject={PermissionSubject.CATEGORY}
                              action="create"
                            >
                              <Button
                                className="mt-4 hover:cursor-pointer"
                                onClick={() => setOpenCreate(true)}
                              >
                                Thêm danh mục
                              </Button>
                            </PermissionGuard>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      categories.map((c) => (
                        <tr
                          key={c._id}
                          className="transition-all hover:bg-gray-50/60"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {c.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {c.slug}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                            {c.canonicalUrl ? (
                              <a
                                href={c.canonicalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline underline-offset-2 hover:text-blue-700"
                                title={c.canonicalUrl}
                              >
                                {c.canonicalUrl}
                              </a>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {c.keyword ? (
                              <div className="w-full flex items-center justify-center gap-1 flex-wrap">
                                {c.keyword
                                  .split(",")
                                  .map((k) => k.trim())
                                  .filter((k) => k.length > 0)
                                  .map((k, idx) => (
                                    <span
                                      key={`${k}-${idx}`}
                                      className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
                                    >
                                      {k}
                                    </span>
                                  ))}
                              </div>
                            ) : (
                              <div className="text-center">-</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {(c.parent as any)?.name || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            <span
                              className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ring-1 ring-inset ${
                                c.level === 0
                                  ? "bg-green-50 text-green-700 ring-green-200"
                                  : c.level === 1
                                  ? "bg-amber-50 text-amber-700 ring-amber-200"
                                  : "bg-purple-50 text-purple-700 ring-purple-200"
                              }`}
                            >
                              {c.level === 0
                                ? "Gốc"
                                : c.level === 1
                                ? "Cấp 1"
                                : "Cấp 2"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="hover:cursor-pointer"
                                onClick={() => {
                                  setSelected(c);
                                  setOpenView(true);
                                }}
                              >
                                Xem
                              </Button>
                              <PermissionGuard
                                subject={PermissionSubject.CATEGORY}
                                action="update"
                              >
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="hover:cursor-pointer"
                                  onClick={() => {
                                    setSelected(c);
                                    setOpenEdit(true);
                                  }}
                                >
                                  Sửa
                                </Button>
                              </PermissionGuard>
                              <PermissionGuard
                                subject={PermissionSubject.CATEGORY}
                                action="delete"
                              >
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:cursor-pointer"
                                  onClick={() => {
                                    setSelected(c);
                                    setOpenDelete(true);
                                  }}
                                >
                                  Xóa
                                </Button>
                              </PermissionGuard>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between w-full">
                    <div>
                      <p className="text-sm text-gray-700">
                        Trang{" "}
                        <span className="font-medium">{pagination.page}</span> /{" "}
                        {pagination.totalPages}
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <Button
                          onClick={() =>
                            handlePage(Math.max(1, pagination.page - 1))
                          }
                          disabled={pagination.page === 1}
                          variant="outline"
                          size="sm"
                          className="rounded-l-md hover:cursor-pointer"
                        >
                          Trước
                        </Button>
                        {Array.from(
                          { length: pagination.totalPages },
                          (_, i) => i + 1
                        ).map((p) => (
                          <Button
                            key={p}
                            onClick={() => handlePage(p)}
                            variant={
                              p === pagination.page ? "default" : "outline"
                            }
                            size="sm"
                            className="rounded-none hover:cursor-pointer"
                          >
                            {p}
                          </Button>
                        ))}
                        <Button
                          onClick={() =>
                            handlePage(
                              Math.min(
                                pagination.totalPages,
                                pagination.page + 1
                              )
                            )
                          }
                          disabled={pagination.page === pagination.totalPages}
                          variant="outline"
                          size="sm"
                          className="rounded-r-md hover:cursor-pointer"
                        >
                          Sau
                        </Button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <ModalCreateCategory
        isOpen={openCreate}
        onClose={() => setOpenCreate(false)}
        onComplete={() =>
          fetchCategories({ page: 1, limit: 10, search: debounced })
        }
      />
      <ModalViewCategory
        isOpen={openView}
        onClose={() => setOpenView(false)}
        categoryId={selected?._id || null}
      />
      <ModalEditCategory
        isOpen={openEdit}
        onClose={() => setOpenEdit(false)}
        categoryId={selected?._id || null}
        onComplete={() =>
          fetchCategories({
            page: pagination.page,
            limit: pagination.limit,
            search: debounced,
          })
        }
      />
      <ModalDeleteCategory
        isOpen={openDelete}
        onClose={() => setOpenDelete(false)}
        categoryId={selected?._id || null}
        onComplete={() =>
          fetchCategories({
            page: pagination.page,
            limit: pagination.limit,
            search: debounced,
          })
        }
      />
    </DashboardLayout>
  );
}
